<script setup>
import { computed, ref, watch } from 'vue';
import { storeToRefs } from 'pinia';
import { useOptionsStore } from '@/stores/options';
import { useLabStore } from '@/stores/lab';
import { isRole } from '@/utils/roleHelper';
import { useUserStore } from '@/stores/user';

// Props and Emits
const props = defineProps({
    lab: {
        type: Object,
        required: true,
    },
    mode: {
        type: String,
        default: 'Edit', // 'Create' or 'Edit'
    },
});
const emit = defineEmits(['saved']);

// Stores and Reactive Data
const { profile } = storeToRefs(useUserStore());
const { store, update } = useLabStore();
const roles = computed(() => isRole(profile.value));
const form = ref({
    addresses: [],
});
const optionsStore = useOptionsStore();
const { labStatusOptions, addressTypeOptions, provinceOptions } = storeToRefs(optionsStore);

// Watchers
watch(() => props.lab, (newLab) => {
    if (newLab && props.mode === 'Edit') {
        form.value = { ...newLab };
        form.value.embedded_markup = Number(form.value.embedded_markup * 100).toFixed(2); // Convert to percentage
        form.value.rx_markup = Number(form.value.rx_markup * 100).toFixed(2); // Convert to percentage
    }
}, { immediate: true, deep: true });

// Logo Upload Logic
const fileInput = ref(null); // Reference to the file input element
const selectedFile = ref(null); // Store the selected file
const selectedFilePreview = ref(null); // Store the preview URL for the selected file

const triggerFileInput = () => {
    fileInput.value.click();
};

const handleFileSelection = (event) => {
    const file = event.target.files[0];
    if (file) {
        selectedFile.value = file; // Store the selected file
        selectedFilePreview.value = URL.createObjectURL(file); // Generate a preview URL
    }
};

// Cleanup old object URLs to avoid memory leaks
watch(selectedFilePreview, (newPreview, oldPreview) => {
    if (oldPreview) {
        URL.revokeObjectURL(oldPreview); // Revoke the old object URL
    }
});

const fileSelection = computed(() => {
    // If a new file is selected, return the preview URL; otherwise, return the existing logo path
    if (selectedFile.value) {
        return selectedFilePreview.value;
    }
    return '/storage/' + form.value.logo_path;
});

// Save Logic
const save = async () => {
    const action = props.mode === 'Create' ? store : update;
    const formData = new FormData();

    // Append all form fields to FormData
    for (const key in form.value) {
        // Can't append array directly to FormData because it will convert it to a string
        if (Array.isArray(form.value[key])) {
            form.value.addresses.forEach((address, index) => {
                for (const key in address) {
                    formData.append(`addresses[${index}][${key}]`, address[key]);
                }
            });
        } else {
            formData.append(key, form.value[key]);
        }
    }

    // Append the selected file if it exists
    if (selectedFile.value) {
        formData.append('logo', selectedFile.value, fileInput.value.files[0]?.name || 'image.png');
    } else {
        formData.append('logo', null);
    }

    // change the embedded markup and rx markup back to decimal, no more than 2 decimal places
    formData.set('embedded_markup', Number(form.value.embedded_markup / 100).toFixed(4));
    formData.set('rx_markup', Number(form.value.rx_markup / 100).toFixed(4));

    await action(formData);
    emit('saved');
};

// Address Management
const addAddress = () => {
    form.value.addresses.push({
        id: '', // can not set to null because it will be converted to string 'null' when appending to formData
        addressable_type: 'App\\Models\\Lab',
        addressable_id: '',
        type_id: '',
        province_id: '',
        address_1: '',
        address_2: '',
        city: '',
        postal_code: '',
    });
};

const removeAddress = (index) => {
    form.value.addresses.splice(index, 1);
};

// Expose Methods
defineExpose({
    save,
});
</script>

<template>
<div class="md:grid md:grid-cols-2 gap-x-4">
    <div class="col-span-full text-lg font-bold mb-6">
        <div>{{ $t('label.logo') }}</div>
        <!-- Display the logo if it exists -->
        <div v-if="form.logo_path || selectedFilePreview" class="w-1/2 border border-gray-400 rounded-xl flex justify-center items-center cursor-pointer hover:scale-105 transform">
            <img :src="fileSelection" class="w-full object-contain">
        </div>
        <!-- Placeholder if no logo is uploaded -->
        <div v-else class="w-48 h-48 bg-gray-200 border border-gray-400 flex justify-center items-center cursor-pointer hover:scale-105 transform">
            {{ $t('label.upload_logo') }}
        </div>
        <input
            ref="fileInput"
            type="file"
            accept="image/*"
            class="hidden"
            @change="handleFileSelection">
        <!-- Button to trigger file input -->
        <StyledButton
            class="mt-2"
            @click="triggerFileInput">
            <FontAwesomeIcon
                icon="upload"
                class="mr-1" />
            {{ $t('label.choose_file') }}
        </StyledButton>
    </div>
    <div class="col-span-full text-lg font-bold">
        {{ $t("label.general") }}
    </div>
    <StyledInput
        v-model="form.name"
        name="name"
        :label="$t('label.name')" />

    <StyledInput
        v-if="roles.systemAdmin() || roles.superAdmin()"
        v-model="form.embedded_markup"
        type="numeric"
        name="embedded_markup"
        :label="$t('label.embedded_markup_percentage')" />

    <StyledInput
        v-if="roles.systemAdmin() || roles.superAdmin()"
        v-model="form.rx_markup"
        type="number"
        name="rx_markup"
        :label="$t('label.rx_markup_percentage')" />

    <StyledInput
        v-if="roles.systemAdmin() || roles.superAdmin()"
        v-model="form.dispensing_fee"
        name="dispensing_fee"
        type="number"
        :label="$t('label.dispensing_fee')" />
    <StyledDropdown
        v-model="form.status_id"
        :options="labStatusOptions"
        name="status"
        :label="$t('label.status')" />
    <div class="col-span-full text-lg font-bold">
        {{ $t("label.contact") }}
    </div>
    <StyledMask
        v-model="form.contact_phone"
        name="contact_phone"
        placeholder="###-###-####"
        :options="{ mask: '###-###-####' }"
        :label="$t('label.contact_phone')" />

    <StyledInput
        v-model="form.contact_first_name"
        name="contact_first_name"
        :label="$t('label.contact_first_name')" />

    <StyledInput
        v-model="form.contact_last_name"
        name="contact_last_name"
        :label="$t('label.contact_last_name')" />

    <StyledInput
        v-model="form.contact_email"
        name="contact_email"
        :label="$t('label.contact_email')" />

    <div class="col-span-full text-lg font-bold">
        {{ $t("label.theme") }}
    </div>
    <VColorPicker
        v-model="form.primary_color"
        name="primary_color"
        :label="$t('label.primary_color')" />

    <VColorPicker
        v-model="form.secondary_color"
        name="secondary_color"
        :title="$t('label.secondary_color')" />

    <div class="col-span-full text-lg font-bold mt-4">
        {{ $t("label.addresses") }}
    </div>
    <div v-for="(address, index) in form.addresses" :key="index" class="border border-grey-200 p-4 mb-4">
        <StyledInput
            v-model="address.address_1"
            name="address_1"
            :label="$t('label.address_1')" />
        <StyledInput
            v-model="address.address_2"
            name="address_2"
            :label="$t('label.address_2')" />
        <StyledInput
            v-model="address.city"
            name="city"
            :label="$t('label.city')" />
        <StyledInput
            v-model="address.postal_code"
            name="postal_code"
            :label="$t('label.postal_code')" />
        <StyledDropdown
            v-model="address.province_id"
            :options="provinceOptions"
            name="province"
            :label="$t('label.province')" />
        <StyledDropdown
            v-model="address.type_id"
            :options="addressTypeOptions"
            name="type"
            :label="$t('label.type')" />
        <div class="col-span-2 justify-self-end"> 
            <StyledButton
                variant="outlined"
                :label="$t('label.remove_address')"
                @click="removeAddress(index)">{{ $t('label.remove_address') }}</StyledButton>
        </div>
    </div>
    <div 
        class="border-2 border-dashed border-gray-300 bg-gray-100 p-4 flex items-center justify-center mb-4"
        style="height: auto; min-height: 150px;">
        <StyledButton
            variant="outlined"
            class="flex items-center gap-2"
            :label="$t('label.add_address')"
            @click="addAddress">
            <FontAwesomeIcon icon="plus" class="mr-1" />
            {{ $t('label.add_address') }}
        </StyledButton>
    </div>
</div>
</template>
